import { reportError } from 'src/utils/reporting/report-errors'

/**
 * The reason codes this frontend asks about.
 *
 * An enum rather than a bare string so a typo cannot compile. Passing
 * `'featuer_disabled'` would otherwise return false forever without anyone
 * noticing - which is precisely the failure this module was written to fix.
 * And since a string enum is not assignable from a literal, every caller has
 * to import the member, which keeps the codes managed in this one place.
 *
 * Not the complete set: the backend defines more than twenty of these via
 * `deny!(:code)` in `app/policies` (22 when this was written), and they are
 * not enumerated in the GraphQL schema, so codegen cannot produce them.
 * Adding one here is a single line.
 */
export enum AuthorizationReason {
  FEATURE_DISABLED = 'feature_disabled',
  POSTING_NOT_PERMITTED = 'posting_not_permitted',
}

// A single string is tolerated next to the documented array: the field is
// hand-built per policy on the backend, and one code is the common case.
const isReasonList = (value: unknown): boolean =>
  typeof value === 'string' ||
  (Array.isArray(value) && value.every((entry) => typeof entry === 'string'))

/**
 * Reads a reason code out of an AuthorizationResult's `reasons.details`.
 *
 * The field is a JSON document keyed by policy identifier, not a bare code -
 * the backend sends something like `{"api/user":["feature_disabled"]}` (see
 * `deny!(:feature_disabled)` in the user policy). So the code always sits
 * inside the document, never is the document.
 *
 * Fails soft rather than throwing: this runs in render paths, and enforcement
 * is server-side anyway. The worst case of a false is an option that is
 * offered and then rejected on submit, which beats taking the screen down.
 *
 * A document that does not match the contract is reported, though. The bug
 * this replaces survived for months because it failed silently, and a format
 * change on the backend would bring back exactly that: a branch that quietly
 * stops rendering. `logToDevConsole` would not do - it is gated on
 * `env === 'development'` and would leave production blind.
 *
 * What that does not cover, and cannot: a renamed code. `feature_disabled`
 * becoming `feature_off` is a valid document containing a value we no longer
 * ask about, and since the schema does not enumerate the codes there is
 * nothing to check it against.
 */
export function hasAuthorizationReason (
  details: string | null | undefined,
  reason: AuthorizationReason
): boolean {
  // Absent is not the same as broken: an unauthenticated or unresolved
  // permission simply carries no details, and that must stay quiet.
  if (details === undefined || details === null || details.trim() === '') {
    return false
  }

  let parsed: unknown

  try {
    parsed = JSON.parse(details)
  } catch (error) {
    reportError(error, { details })
    return false
  }

  // Arrays are excluded on purpose, not by oversight: `Object.values` on one
  // returns its elements, so a bare `["feature_disabled"]` would match while
  // not being the documented shape.
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    reportError(
      new Error('AuthorizationResult reasons.details is not a JSON object'),
      { details }
    )
    return false
  }

  // Watching the top-level shape alone would have been half a guard: a
  // backend that starts sending `{"api/user": {"codes": [...]}}` still parses
  // into an object, and the lookup below would just return false for ever -
  // the same silent failure this whole module exists to prevent. Reported
  // rather than aborted, so a second policy carrying a well-formed list is
  // still evaluated.
  const values = Object.values(parsed)

  if (!values.every(isReasonList)) {
    reportError(
      new Error(
        'AuthorizationResult reasons.details holds a value that is neither a code nor a list of codes'
      ),
      { details }
    )
  }

  return values.flat().includes(reason)
}
